import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Mail, Building2, Phone, User, MessageSquare } from 'lucide-react';
import { contactFormData } from '../mockData';

export const ContactSection = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    phone: '',
    userType: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({
      ...prev,
      userType: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) throw new Error('Failed to send');
      
      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          fullName: '',
          email: '',
          company: '',
          phone: '',
          userType: '',
          message: ''
        });
      }, 3000);
    } catch (err) {
      setSubmitError('Failed to send your message. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gray-50 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-0 w-96 h-96 bg-red-100/50 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-0 w-96 h-96 bg-red-100/50 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            {contactFormData.headline}
          </h2>
          <div className="w-20 h-1 bg-red-600 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {contactFormData.description}
          </p>
        </div>

        {/* Contact Form Card */}
        <Card className="bg-white border-gray-200 shadow-xl">
          <CardHeader className="pb-8">
            <CardTitle className="text-2xl text-gray-900 text-center">
              Get Started Today
            </CardTitle>
            <CardDescription className="text-center text-gray-600 text-base">
              Fill out the form below and we'll get back to you within 24 hours
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isSubmitted ? (
              <div className="py-12 text-center">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Thank You!
                </h3>
                <p className="text-gray-600 text-lg">
                  {contactFormData.successMessage}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Full Name */}
                  <div className="space-y-2">
                    <Label htmlFor="fullName" className="text-gray-900 flex items-center gap-2">
                      <User size={16} className="text-red-600" />
                      Full Name *
                    </Label>
                    <Input
                      id="fullName"
                      name="fullName"
                      type="text"
                      required
                      value={formData.fullName}
                      onChange={handleChange}
                      className="bg-white border-gray-300 text-gray-900 focus:border-red-600"
                      placeholder="John Doe"
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-900 flex items-center gap-2">
                      <Mail size={16} className="text-red-600" />
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="bg-white border-gray-300 text-gray-900 focus:border-red-600"
                      placeholder="john@company.com"
                    />
                  </div>

                  {/* Company */}
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-gray-900 flex items-center gap-2">
                      <Building2 size={16} className="text-red-600" />
                      Company *
                    </Label>
                    <Input
                      id="company"
                      name="company"
                      type="text"
                      required
                      value={formData.company}
                      onChange={handleChange}
                      className="bg-white border-gray-300 text-gray-900 focus:border-red-600"
                      placeholder="Your Company Name"
                    />
                  </div>

                  {/* Phone */}
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-gray-900 flex items-center gap-2">
                      <Phone size={16} className="text-red-600" />
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="bg-white border-gray-300 text-gray-900 focus:border-red-600"
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                </div>

                {/* User Type */}
                <div className="space-y-2">
                  <Label htmlFor="userType" className="text-gray-900 flex items-center gap-2">
                    <MessageSquare size={16} className="text-red-600" />
                    I am a *
                  </Label>
                  <Select value={formData.userType} onValueChange={handleSelectChange} required>
                    <SelectTrigger className="bg-white border-gray-300 text-gray-900 focus:border-red-600">
                      <SelectValue placeholder="Select your role" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      <SelectItem value="Fleet" className="text-gray-900">Fleet Operator</SelectItem>
                      <SelectItem value="OEM" className="text-gray-900">OEM</SelectItem>
                      <SelectItem value="Partner" className="text-gray-900">Potential Partner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-gray-900">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    className="bg-white border-gray-300 text-gray-900 focus:border-red-600 min-h-32"
                    placeholder="Tell us about your needs or investment interest..."
                  />
                </div>

                {/* Submit Button */}
                <Button 
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? 'Sending...' : contactFormData.submitText}
                </Button>
                {submitError && (
                  <p className="text-red-600 text-center text-sm mt-2" data-testid="contact-error">{submitError}</p>
                )}
              </form>
            )}
          </CardContent>
        </Card>

        {/* Additional Contact Info */}
        <div className="mt-12 text-center">
          <p className="text-gray-600">
            Prefer email? Reach us at{' '}
            <a href="mailto:steve@axicle.com" className="text-red-600 hover:text-red-700 transition-colors duration-200 font-semibold">
              steve@axicle.com
            </a>
          </p>
        </div>

        {/* Truck side view */}
        <div className="mt-12 max-w-3xl mx-auto">
          <img 
            src="https://customer-assets.emergentagent.com/job_38babe41-8d78-47cc-a112-99a9deb87de8/artifacts/d0wl4zrl_Truck%20%26%20Trailer%20-%20Side%20View%2001.png"
            alt="Axicle Robotics - Robotic Coupling System installed on truck"
            className="w-full h-auto object-contain"
          />
        </div>
      </div>
    </section>
  );
};
